/**
 * Share DEK Rotation
 *
 * Rotates the Document Encryption Key (DEK) for a share when a member is
 * removed or revoked. Optionally revokes users atomically in the same request.
 *
 * Flow:
 * 1. Merge current Y.Doc state from memory, local persistence and the server
 * 2. Fetch baseline_hash from server (CAS token)
 * 3. Generate new random DEK
 * 4. Re-encrypt state with new DEK → new_baseline
 * 5. Use remaining members' authenticated public keys, wrap new DEK for each
 * 6. Build attachment rewrap list: list current attachments, unwrap each BEK
 *    with old DEK, rewrap with new DEK, re-encrypt filename ciphertext
 * 7. POST /api/shares/:id/rotate (ATOMIC: snapshot + revoke + baseline +
 *    wrapped_deks + invitations + attachment BEK rewraps + broadcast)
 * 8. Retain old wrapped keys and record the new key/authority in the vault
 *
 * Atomicity invariant: attachment BEK rewrap MUST be folded into the /rotate
 * request body, not done in a separate POST after commit. The prior version
 * called PATCH /blobs/rekey after /rotate succeeded, leaving a window where
 * any transient failure (network blip, tab close) would commit the baseline
 * under the new DEK while attachments stayed wrapped under the old DEK.
 * See the bug filed 2026-05 against
 * production for the first observed instance.
 *
 * A revocation includes the merged pre-rotation state encrypted with the old
 * DEK. The server stores it atomically with the rotation for later recovery.
 */

import * as Y from "yjs";
import { Keypair } from "../crypto/keypair.js";
import { readVaultFile } from "../storage/index.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import * as MapKeyTrust from '../crypto/map-key-trust.js';
import { getSharedDocManager } from "./share-lifecycle.js";
import { getLinkedDocManager, getDocManager } from "./crdt-bridge.js";
import { encryptPayload } from "./ws-encryption.js";
import { encryptInviteTitle } from "../crypto/invite-title.js";
import { encryptLinkSecret, decryptLinkSecret } from "../crypto/link-keys.js";
import { mergeBackupState } from "../crypto/backup-state.js";
import { getCurrentUser } from "../auth/handlers.js";
import { emit, Events } from "../core/events.js";
import { createLogger } from "../core/logger.js";
import {
  listAttachments,
  uint8ToBase64,
  base64ToUint8,
} from "../services/attachment-service.js";
import { rekeyBek, rekeyFilename } from "../crypto/attachment-crypto.js";
import { uploadWithProgress } from "../../shared/utils/transfer-progress.js";
import { t } from "../../shared/i18n/index.js";

const log = createLogger("ShareRotation");

/**
 * Rotate the DEK for a share, optionally revoking users atomically.
 *
 * Includes all readable local/server state in the revocation snapshot.
 *
 * @param {string} shareId - The share UUID
 * @param {Object} [options]
 * @param {string[]} [options.revokeUserIds] - User IDs to revoke in this rotation
 * @param {Function} [options.report] - progress callback ({ label, current, total, unit })
 * @param {AbortSignal} [options.signal] - cancels the rotation until its upload
 *   is complete. Nothing is stored on the server or in the vault before the
 *   single request succeeds, so a cancel changes nothing (AbortError).
 * @param {Function} [options.setCancellable] - told when cancelling stops
 *   being safe (upload complete: the server may already be applying it)
 * @throws {Error} If rotation fails (caller should handle or display warning)
 */
export async function rotateShareDEK(shareId, { revokeUserIds = [], turnOffLink = false, report = () => {}, signal = null, setCancellable = () => {} } = {}) {
  const ctx = { report, signal, setCancellable, held: false, renewal: null, turnOffLink };
  let done = false;
  try {
    await _doRotate(shareId, 0, revokeUserIds, ctx);
    done = true;
  } finally {
    clearInterval(ctx.renewal);
    // The server ends the hold when the key change commits; given up or
    // cancelled, the map's saves go on at once.
    if (ctx.held && !done) _releaseSaves(shareId);
  }
}

/** The hold lasts 60 s: renewed every 20 s while the key change runs (a
 *  large map, many attachments, a slow upload). */
const HOLD_RENEW_MS = 20000;
/** A hold request that hangs is given up: the key change goes on without. */
const HOLD_REQUEST_MS = 10000;

/**
 * Hold the map's saves while its key changes (`POST /shares/:id/rotation-hold`,
 * 60 s, renewed by each call): changes made meanwhile are refused as busy and
 * resent by their writers under the new key, so none can make the key change
 * fail (before, each one refused it and after 3 tries it gave up: removing
 * someone from a busy map failed). Owner only; an admin, or a server without
 * holds, changes the key as before (false).
 */
async function _holdSaves(shareId) {
  try {
    const response = await fetch(`/api/shares/${shareId}/rotation-hold`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal: AbortSignal.timeout(HOLD_REQUEST_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
}

function _releaseSaves(shareId) {
  fetch(`/api/shares/${shareId}/rotation-hold`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  }).catch(() => { /* the hold ends by itself within 60 s */ });
}

/**
 * Before deleting a map its owner shares: every member removed at once (one
 * key change, each keeping a copy of the map: sharing.md, "Revocation"); the
 * server deletes no map that still has members. Deleting an online map needs
 * the server: nothing is deleted without it. Used by every interface
 * (engine.maps.delete; until 2026-10-26 only classic's vault did this, and a
 * shared map deleted through the engine API was refused).
 * @param {string} mapId
 */
export async function removeAllMembers(mapId) {
  let response;
  try {
    response = await fetch(`/api/shares/${mapId}/permissions`, { credentials: "include" });
  } catch {
    throw new Error(t("vault.storageError.deleteNeedsConnection"));
  }
  if (response.status === 404) return; // never shared
  if (!response.ok) throw new Error(t("vault.map.deletePermissionsFailed"));
  const members = (await response.json()).filter((p) => p.role !== "owner").map((p) => p.user_id);
  // Its link goes too (deleting the map deletes it anyway).
  if (members.length) await rotateShareDEK(mapId, { revokeUserIds: members, turnOffLink: true });
}

/** Stop here if the user cancelled (only called before the upload completes). */
function _checkCancelled(ctx) {
  if (ctx.signal?.aborted) throw new DOMException('Rotation cancelled', 'AbortError');
}

const MAX_CAS_RETRIES = 2;

async function _doRotate(shareId, attempt, revokeUserIds, ctx) {
  ctx.setCancellable(true);
  ctx.report({ label: t('share.rotation.checking') });
  const authority = await MapKeyTrust.ensure(shareId);
  _checkCancelled(ctx);
  // 0. Hold the map's saves before reading it: nothing lands until the
  //    new key is in place.
  if (await _holdSaves(shareId) && !ctx.held) {
    ctx.held = true;
    ctx.renewal = setInterval(() => _holdSaves(shareId), HOLD_RENEW_MS);
  }
  _checkCancelled(ctx);
  // 1. Merge every available source of current Y.Doc state (plaintext).
  ctx.report({ label: t('share.rotation.reading') });
  const { state: stateBytes, observedPayloads } = await _getDocState(shareId);
  _checkCancelled(ctx);
  if (!stateBytes || stateBytes.length === 0) {
    throw new Error(t("share.rotation.noState", { id: shareId }));
  }

  // 2. Fetch baseline hash (CAS token) from server
  const hashResp = await fetch(`/api/shares/${shareId}/baseline-hash`, {
    credentials: "include",
  });
  if (!hashResp.ok) {
    throw new Error(t("share.rotation.baselineFailed", { status: String(hashResp.status) }));
  }
  const { baseline_hash } = await hashResp.json();

  // 3. Generate new DEK (32 random bytes)
  const newDEKBytes = Keypair.generateDEK();
  let oldDekBytes;
  try {
    const newDEK = await crypto.subtle.importKey(
      "raw", newDEKBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"],
    );

    // 4. Re-encrypt Y.Doc state with new DEK → new_baseline (same format as WS updates)
    ctx.report({ label: t('share.rotation.encrypting') });
    const newBaselineB64 = await encryptPayload(stateBytes, newDEK, shareId);
    _checkCancelled(ctx);

    // 5. Fetch remaining (non-revoked) members and wrap new DEK for each.
    //    Exclude users being revoked in this rotation — they must NOT get the new DEK.
    const revokeSet = new Set(revokeUserIds);

    // L-4: Retry on transient failure
    let permsResp;
    for (let fetchAttempt = 0; fetchAttempt < 3; fetchAttempt++) {
      permsResp = await fetch(`/api/shares/${shareId}/permissions`, { credentials: "include" });
      if (permsResp.ok) break;
      if (fetchAttempt < 2) {
        await new Promise((r) => setTimeout(r, 1000 * (fetchAttempt + 1)));
      }
    }
    if (!permsResp.ok) {
      throw new Error(t("share.rotation.permissionsRetryFailed", { status: String(permsResp.status) }));
    }
    const permissions = await permsResp.json();

    const wrappedDeks = {};

    for (const perm of permissions) {
      // Skip users being revoked — they keep their old wrapped DEK for snapshot decryption
      if (revokeSet.has(perm.user_id)) continue;

      // H-2: Abort if any remaining member lacks a username (can't look up public key)
      if (!perm.username) {
        throw new Error(t("share.rotation.memberNoUsername", { id: perm.user_id }));
      }
      try {
        const trustedMember = authority.members.find(m => m.userId === perm.user_id);
        if (!trustedMember || trustedMember.role !== perm.role) throw new Error(t('share.rotation.membershipDiffers'));
        const pubKey = Keypair.fromBase64(trustedMember.publicKey);
        const wrapped = await Keypair.wrapDEKAnonymous(newDEKBytes, pubKey);
        wrappedDeks[perm.user_id] = Keypair.toBase64(wrapped);
      } catch (e) {
        // H-2: Also abort if public-key lookup fails
        throw new Error(t("share.rotation.wrapFailed", { name: perm.username, error: e.message }));
      }
    }

    // Also wrap for self as safety guard
    const currentUser = getCurrentUser();
    const selfPubKeyData = VaultStorage.getIndex()?.keypair?.publicKey;
    if (currentUser && selfPubKeyData && !wrappedDeks[currentUser.user_id]) {
      const selfPubKey = Keypair.fromBase64(selfPubKeyData);
      const selfWrapped = await Keypair.wrapDEKAnonymous(newDEKBytes, selfPubKey);
      wrappedDeks[currentUser.user_id] = Keypair.toBase64(selfWrapped);
    }

    // 5b. Re-wrap new DEK for surviving pending invitees.
    //     After rotation, pending invitations' wrapped_deks would be stale (old DEK).
    //     We re-wrap the new DEK for each invitee not being revoked and send it to the
    //     server so it can update the invitation rows instead of deleting them.
    const rewrappedInvitations = {};
    const invResp = await fetch(`/api/shares/${shareId}/invitations`, { credentials: "include" });
    if (!invResp.ok) throw new Error(t('share.rotation.invitationsUnreadable'));
    const invitations = await invResp.json();
    const invitedIds = new Set();
    for (const inv of invitations) {
      const trustedInvitee = authority.members.find(m => m.userId === inv.invitee_id);
      if (!trustedInvitee || trustedInvitee.role !== inv.role) {
        throw new Error(t('share.rotation.invitationDiffers'));
      }
      if (revokeSet.has(trustedInvitee.userId)) continue;
      invitedIds.add(trustedInvitee.userId);
      const wrapped = await Keypair.wrapDEKAnonymous(newDEKBytes, Keypair.fromBase64(trustedInvitee.publicKey));
      rewrappedInvitations[inv.id] = Keypair.toBase64(wrapped);
    }
    // Their notifications' title under the new key too: under the old one,
    // the invitee's app could not read it once its key was rewrapped.
    const ownTitle = VaultStorage.getDoc?.(shareId)?.title;
    const invitationTitle = Object.keys(rewrappedInvitations).length && ownTitle
      ? await encryptInviteTitle(newDEKBytes, shareId, ownTitle)
      : null;

    // 6. Build attachment BEK rewraps under the new DEK. This MUST happen
    //    before the /rotate POST so the server can apply baseline + wrapped_deks
    //    + attachment rewraps in one DB transaction. Doing it after the commit
    //    (the old code path) left a window where a network blip or tab close
    //    would orphan the attachments under the soon-to-be-zeroed old DEK.
    //
    //    Hard-fail policy: if listAttachments or any single rewrap fails, we
    //    abort the rotation entirely and do NOT zero the old DEK. Better to
    //    leave the share unrotated than to commit a rotation that loses data.
    //
    //    On CAS retry the list is rebuilt from scratch — this is essential
    //    because the retry might happen because a concurrent upload added an
    //    attachment, and the server enforces that the rewrap set covers every
    //    current ref.
    oldDekBytes = await VaultStorage.getDocDEK(shareId);
    const oldDek = await crypto.subtle.importKey(
      "raw", oldDekBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"],
    );
    const attachmentRewraps = await _buildAttachmentRewraps(shareId, oldDek, newDEK, ctx);
    _checkCancelled(ctx);

    // 6a. The map's link (docs/plans/2026-10-28-link-sharing.md): turned off
    //     (out of the key log: the new key is not sealed for it), or kept, its
    //     secret and the map's title encrypted under the new key.
    const linkResp = await fetch(`/api/shares/${shareId}/link`, { credentials: "include" });
    if (!linkResp.ok && linkResp.status !== 404) throw new Error(t("share.rotation.linkUnreadable"));
    const activeLink = linkResp.ok ? await linkResp.json() : null; // 404: a server before links
    let linkChange = null;
    if (activeLink && ctx.turnOffLink) {
      linkChange = { turn_off: true };
    } else if (activeLink) {
      if (!activeLink.encrypted_secret) throw new Error(t("share.rotation.linkUnreadable"));
      const secret = await decryptLinkSecret(oldDekBytes, shareId, activeLink.id, activeLink.encrypted_secret);
      try {
        linkChange = {
          encrypted_secret: await encryptLinkSecret(newDEKBytes, shareId, activeLink.id, secret),
          ...(ownTitle && { encrypted_title: await encryptInviteTitle(newDEKBytes, shareId, ownTitle) }),
        };
      } finally { secret.fill(0); }
    }
    const keptLink = activeLink && !ctx.turnOffLink ? activeLink.id : null;

    // 6b. Build pre-rotation snapshot for revocation recovery, encrypted under the
    //     OLD DEK. Uses the SAME `stateBytes` that feeds `new_baseline`, so the
    //     revocation snapshot captures the full document state (baseline + un-
    //     compacted deltas) — not just the stale baseline column the server
    //     used to copy.
    //
    //     Without this, any edits that happened since the last compaction got
    //     rolled into `new_baseline` (encrypted under the NEW DEK the revoked
    //     user can't read) but were MISSING from the revocation snapshot. The
    //     revoked user's recovery silently lost those edits.
    //
    //     Only computed when we're actually revoking somebody. Pure rotation
    //     (no revocations) doesn't need a snapshot.
    let preRotationSnapshotB64 = null;
    if (revokeUserIds.length > 0) {
      ctx.report({ label: t('share.rotation.preparingRecovery') });
      preRotationSnapshotB64 = await encryptPayload(stateBytes, oldDek, shareId);
    }

    // 7. POST /rotate — atomic: revokes + snapshot + baseline + wrapped_deks +
    //    invitations + attachment BEK rewraps + broadcast.
    const activeIds = new Set(permissions.map(p => p.user_id));
    const members = authority.members.filter(m => !revokeSet.has(m.userId) &&
      (activeIds.has(m.userId) || invitedIds.has(m.userId) || (m.link && m.userId === keptLink))).map(m =>
      m.pending && activeIds.has(m.userId) ? { ...m, pending: false } : m);
    const key_authority = await MapKeyTrust.prepare(shareId, 'rotate', members, newDEKBytes, authority);
    _checkCancelled(ctx);
    // One request applies everything or nothing. Cancelling is safe until
    // every byte is sent; after that the server may already be applying it.
    const resp = await uploadWithProgress(`/api/shares/${shareId}/rotate`, {
      method: "POST",
      credentials: "include",
      signal: ctx.signal || undefined,
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({
        baseline_hash,
        observed_payloads: observedPayloads,
        new_baseline: newBaselineB64,
        wrapped_deks: wrappedDeks,
        key_authority,
        revoke_user_ids: revokeUserIds,
        rewrapped_invitations: rewrappedInvitations,
        ...(invitationTitle && { invitation_title: invitationTitle }),
        ...(linkChange && { link: linkChange }),
        attachment_rewraps: attachmentRewraps,
        pre_rotation_snapshot: preRotationSnapshotB64,
      }),
    }, (progress) => {
      if (progress.uploaded || progress.tracked === false) ctx.setCancellable(false);
      ctx.report(progress.uploaded
        ? { label: t('share.rotation.saving') }
        : { ...progress, label: t('share.rotation.uploading') });
    });
    ctx.setCancellable(false);

    if (resp.status === 409) {
      const conflict = await resp.clone().json().catch(() => ({}));
      // Not a race: retrying cannot help (too many pending recoveries, or
      // this client must reload). Show the server's reason.
      if (/Too many active revocation snapshots|Reload Orbital before rotating/i.test(conflict.error || "")) {
        throw new Error(conflict.error);
      }
      if (attempt >= MAX_CAS_RETRIES) {
        throw new Error(t("share.rotation.casGaveUp", { retries: MAX_CAS_RETRIES }));
      }
      log.warn(`CAS conflict, retrying (attempt ${attempt + 1}/${MAX_CAS_RETRIES})...`);
      // Refused: nothing was applied, so cancelling is safe again.
      return _doRotate(shareId, attempt + 1, revokeUserIds, ctx);
    }

    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({}));
      throw new Error(errBody.error || t("share.rotation.requestFailed", { status: String(resp.status) }));
    }

    // 8. Update local vault with new wrapped DEK for self (before the WS reconnect fires)
    if (currentUser && wrappedDeks[currentUser.user_id]) {
      await VaultStorage.updateDocWrappedDEK(shareId, wrappedDeks[currentUser.user_id], { verifiedAuthority: key_authority.state });
      VaultStorage.clearDEKCache(shareId);
    }

    log.info("DEK rotation complete for share:", shareId,
      revokeUserIds.length > 0 ? `(revoked ${revokeUserIds.length} users)` : "",
      attachmentRewraps.length > 0 ? `(${attachmentRewraps.length} attachments rewrapped)` : "");
  } finally { newDEKBytes.fill(0); oldDekBytes?.fill(0); }
}

/**
 * List the share's current attachments and rewrap every BEK + filename
 * ciphertext under the new DEK. Returns the request body shape expected by
 * the server's /rotate endpoint.
 *
 * Hard-fails (throws) if anything goes wrong: a missing listAttachments
 * response, an unrewrappable BEK, or a malformed legacy filename ciphertext.
 * Caller must NOT proceed with the rotation in that case — committing a
 * rotation without a full rewrap list leaves attachments using the old DEK.
 *
 * @param {string} shareId
 * @param {CryptoKey} oldDek
 * @param {CryptoKey} newDek
 * @returns {Promise<Array<{ blob_id, wrapped_bek, filename_encrypted: string|null }>>}
 */
async function _buildAttachmentRewraps(shareId, oldDek, newDek, ctx = { report() {}, signal: null }) {
  // listAttachments returns wrapped_bek base64 + filename_encrypted base64.
  // No DEK argument needed here — we don't care about plaintext filenames,
  // only about re-encrypting the ciphertext under newDek.
  const attachments = await listAttachments(shareId);
  if (attachments.length === 0) return [];

  const rewraps = [];
  for (const a of attachments) {
    if (ctx.signal?.aborted) throw new DOMException('Rotation cancelled', 'AbortError');
    ctx.report({ label: t('share.rotation.relocking'), current: rewraps.length, total: attachments.length });
    const oldWrapped = base64ToUint8(a.wrappedBek);
    // rekeyBek throws on AES-GCM auth failure → caller aborts. Don't swallow.
    const newWrapped = await rekeyBek(oldDek, newDek, oldWrapped);

    let newFilenameEncryptedB64 = null;
    if (a.filenameEncrypted) {
      const oldFn = base64ToUint8(a.filenameEncrypted);
      const rewrapped = await rekeyFilename(oldDek, newDek, oldFn);
      // rekeyFilename returns null for unrecoverable legacy ciphertexts.
      // Abort before committing if the filename cannot be preserved.
      if (!rewrapped) throw new Error(t('share.rotation.filenameLost'));
      newFilenameEncryptedB64 = uint8ToBase64(rewrapped);
    }

    rewraps.push({
      blob_id: a.attachmentId,
      wrapped_bek: uint8ToBase64(newWrapped),
      filename_encrypted: newFilenameEncryptedB64,
    });
  }
  return rewraps;
}

// ============================================
// State Loading Helpers
// ============================================

/**
 * Merge all available memory, filesystem/IndexedDB and server state. Refuse
 * rotation if any source is unreadable, including any individual delta.
 *
 * @param {string} shareId
 * @returns {Promise<{state: Uint8Array|null, observedPayloads: string[]}>}
 */
async function _getDocState(shareId) {
  const localDoc = new Y.Doc();
  const rawKey = await VaultStorage.getDocDEK(shareId);
  try {
    const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['decrypt']);
    const main = getDocManager();
    const managers = new Set([getSharedDocManager(shareId), getLinkedDocManager(shareId),
      main?.docId === shareId ? main : null]);
    for (const manager of managers) {
      if (manager?.doc) Y.applyUpdate(localDoc, Y.encodeStateAsUpdate(manager.doc));
    }
    if (globalThis.window?.__TAURI_INTERNALS__?.invoke) {
      for (const kind of ['main', 'cache']) {
        const bytes = await readVaultFile(kind, shareId);
        if (bytes.length) Y.applyUpdate(localDoc, bytes);
      }
    }
    const persisted = await _loadFromIndexedDB(shareId);
    if (persisted) Y.applyUpdate(localDoc, persisted);
    const response = await fetch(`/api/docs/${shareId}/state`, { credentials: 'include' });
    if (!response.ok) throw new Error(t('share.rotation.serverStateUnreadable'));
    const server = await response.json();
    const result = await mergeBackupState(Y.encodeStateAsUpdate(localDoc), server, key, shareId);
    if (result.warnings.length) throw new Error(t('share.rotation.updateUnreadable'));
    const records = [...(server.baseline ? [server.baseline] : []), ...(server.deltas || [])];
    const observedPayloads = await Promise.all(records.map(async payload => {
      const bytes = Uint8Array.from(atob(payload), char => char.charCodeAt(0));
      const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
      return [...hash].map(byte => byte.toString(16).padStart(2, '0')).join('');
    }));
    return { state: result.state, observedPayloads };
  } finally { rawKey.fill(0); localDoc.destroy(); }
}

/**
 * Try loading Yjs state from IndexedDB.
 */
async function _loadFromIndexedDB(shareId) {
  let persistence = null;
  let tempDoc = null;
  try {
    const { IndexeddbPersistence } = await import("y-indexeddb");
    tempDoc = new Y.Doc();
    persistence = new IndexeddbPersistence(`orbital-${shareId}`, tempDoc);
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Timeout")), 5000);
      persistence.once("synced", () => { clearTimeout(timeout); resolve(); });
    });
    const state = Y.encodeStateAsUpdate(tempDoc);
    if (state && state.length > 2) {
      log.debug("Loaded state from IndexedDB for rotation");
      return state;
    }
  } catch (e) {
    throw new Error(t('share.rotation.localStateUnreadable', { error: e.message }));
  } finally {
    // Always release the temp IDB connection — the timeout path used to leak
    // it, which could make OTHER tabs' loads on the same store time out too.
    try { persistence?.destroy(); } catch { /* already closed */ }
    try { tempDoc?.destroy(); } catch { /* already destroyed */ }
  }
  return null;
}
